import React, { forwardRef } from "react";
import { Typography } from "antd";
import { SellInvoice } from "../../types/sale";
import { ReturnInvoice } from "../../types/return";
import { PurchaseInvoice } from "../../types/purchase";
import logo from "../../assets/servicelogo.jpg";
import logo_ from "../../assets/felicitysolarlogo.jpg";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../store/currencyContext";

const LOGO_URL = logo;
const logoURL = logo_;
const { Title, Text } = Typography;

interface PrintableInvoiceProps {
    type: "sale" | "return" | "purchase";
    invoice: SellInvoice | ReturnInvoice | PurchaseInvoice;
    getCustomerOrSupplierName: () => string;
    getProductName: (id: string) => string;
}

const PrintableInvoice = forwardRef<HTMLDivElement, PrintableInvoiceProps>(
    ({ type, invoice, getCustomerOrSupplierName, getProductName }, ref) => {
        const { t, i18n } = useTranslation();
        const { currencies } = useCurrency();
        const isReturn = type === "return";
        const isPurchase = type === "purchase";
        const isSale = type === "sale";
        const isRTL = ["ar", "ckb"].includes(i18n.language);

        // Exchange rate: find rate for the invoice currency to IQD
        const currency = invoice.currency ?? "IQD";
        const currencyRecord = currencies.find(c => c.code === currency);
        const rateToIQD = currencyRecord?.rateToIQD ?? 1;
        const convertedTotal = currency !== "IQD"
            ? invoice.total * rateToIQD
            : null;
        const convertedCurrency = currency !== "IQD" ? "IQD" : null;
        // Also convert from IQD to USD equivalent if invoice is in IQD
        const usdRecord = currencies.find(c => c.code === "USD");
        const iqd2usd = usdRecord?.rateToIQD ?? null;
        const convertedToUSD = currency === "IQD" && iqd2usd
            ? invoice.total / iqd2usd
            : null;

        // Whole-invoice discount (fixed amount). invoice.total is already the net (after discount),
        // so the subtotal shown to the customer is total + discount.
        const discountAmount = Number((invoice as any).discount) || 0;
        const subtotalAmount = (invoice.total || 0) + discountAmount;

        const items = invoice.items.map((item: any, index: number) => {
            let productName = "Unknown Product";
            if (item.product) {
                if (typeof item.product === "object" && item.product !== null && item.product.name) {
                    productName = item.product.name;
                } else {
                    // product is a plain string ID — resolve via callback
                    const resolved = getProductName(
                        typeof item.product === "string" ? item.product : (item.product._id || item.product.id || "")
                    );
                    if (resolved && resolved !== "-") productName = resolved;
                }
            }
            const qty = Number(item.qty) || 0;
            const price = Number(isPurchase ? item.buyPrice : item.sellPrice) || 0;
            // Per-line discount, when the line carries one, comes off the line total.
            const lineDiscount = Number(item.discount) || 0;
            return {
                key: index,
                name: productName,
                unit: item.unit || "piece",
                qty: item.qty || 0,
                length: item.unitLength || "-",
                price: isPurchase ? item.buyPrice : item.sellPrice,
                discount: item.discount || "-",
                lineTotal: Math.max(qty * price - lineDiscount, 0),
            };
        });

        // Sum of the item lines, before the whole-invoice discount.
        const itemsTotal = items.reduce((sum, item) => sum + item.lineTotal, 0);

        const headerTitle = t(`header.${type}`);
        const partyLabel = isPurchase ? t("supplier") : t("customer");

        // Resolve customer / supplier name, falling back to the passed callback when the
        // field is a plain ObjectId string (not a populated object).
        let customerName = '-';
        if ("customer" in invoice && invoice.customer) {
            if (typeof invoice.customer === "object" && invoice.customer !== null && "name" in invoice.customer) {
                customerName = (invoice.customer as { name?: string }).name || getCustomerOrSupplierName() || '-';
            } else {
                // string ID — use the callback which knows how to look it up
                customerName = getCustomerOrSupplierName() || '-';
            }
        } else if ("supplier" in invoice && invoice.supplier) {
            if (typeof invoice.supplier === "object" && invoice.supplier !== null && "name" in invoice.supplier) {
                customerName = (invoice.supplier as { name?: string }).name || getCustomerOrSupplierName() || '-';
            } else {
                customerName = getCustomerOrSupplierName() || '-';
            }
        }


        return (
            <div
                ref={ref}
                style={{
                    width: "210mm",
                    height: "297mm",
                    maxWidth: "210mm",
                    maxHeight: "297mm",
                    margin: "0 auto",
                    background: "linear-gradient(135deg, #F7F9FC 0%, #e3f4fd 100%)",
                    padding: "10mm",
                    fontFamily: isRTL ? "Arial, 'Segoe UI', Tahoma, sans-serif" : "Inter, Arial, sans-serif",
                    color: "#262643",
                    boxSizing: "border-box",
                    position: "relative",
                    direction: isRTL ? 'rtl' : 'ltr',
                }}
            >
                <style>
                    {`
                    @media print {
                        @page { 
                            size: A4; 
                            margin: 0; 
                        }
                        body { 
                            background: none !important; 
                            -webkit-print-color-adjust: exact;
                            font-family: Arial, 'Segoe UI', Tahoma, sans-serif;
                        }
                        .invoice-container {
                            width: 210mm !important;
                            height: 297mm !important;
                            padding: 10mm !important;
                            margin: 0 !important;
                        }
                        .invoice-watermark { 
                            display: block !important; 
                        }
                        img { 
                            -webkit-print-color-adjust: exact !important;
                            color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        * {
                            font-family: ${isRTL ? "Arial, 'Segoe UI', Tahoma, sans-serif" : "Inter, Arial, sans-serif"} !important;
                        }
                    }
                    @media screen {
                        .invoice-container {
                            box-shadow: 0 0 20px rgba(0,0,0,0.1);
                            margin: 20px auto;
                        }
                    }
                    .invoice-watermark {
                        position: absolute;
                        top: 44%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(${isRTL ? '18deg' : '-18deg'});
                        opacity: 0.08;
                        font-size: 6rem;
                        font-weight: 900;
                        color: #2850a7;
                        z-index: 0;
                        letter-spacing: ${isRTL ? '3px' : '5px'};
                        pointer-events: none;
                        user-select: none;
                        white-space: nowrap;
                        display: none;
                        font-family: ${isRTL ? "Arial, 'Segoe UI', Tahoma, sans-serif" : "Inter, Arial, sans-serif"};
                        direction: ${isRTL ? 'rtl' : 'ltr'};
                    }
                    .invoice-header-bar {
                        box-shadow: 0 5px 32px #d4e3f5;
                        background: #fff;
                        border-bottom: 3px solid #2850A7;
                        display: flex;
                        align-items: center;
                        padding: 0 44px 0 44px;
                        min-height: 116px;
                        border-radius: 0 0 22px 22px;
                        margin-bottom: 0;
                        flex-direction: ${isRTL ? "row-reverse" : "row"};
                    }
                    .invoice-logo-img {
                        width: 82px;
                        height: 82px;
                        border-radius: 14px;
                        object-fit: contain;
                        background: #fff;
                        border: 2px solid #f2f3fa;
                        ${isRTL
                            ? "margin-left: 36px; margin-right: 0;"
                            : "margin-right: 36px; margin-left: 0;"}
                        box-shadow: 0 1px 16px rgba(40,80,167,0.08);
                        padding: 6px;
                    }
                    .invoice-header-content {
                        flex: 1;
                        text-align: ${isRTL ? "right" : "left"};
                        padding-top: 12px;
                    }
                    .invoice-status {
                        display: flex;
                        align-items: center;
                        justify-content: ${isRTL ? "flex-start" : "flex-end"};
                        color: #2550c0;
                        font-size: 1.4rem;
                        font-weight: 900;
                        background: #e7eefe;
                        border-radius: 15px;
                        padding: 8px 34px;
                        border: 2px dashed #b6c3ed;
                        ${isRTL ? "margin-right: 15px; margin-left: 0;" : "margin-left: 15px; margin-right: 0;"}
                        letter-spacing: 2px;
                        box-shadow: 0 1px 8px #d3f8ef33;
                        min-width: 120px;
                    }
                    .invoice-main-content {
                        background: #fff;
                        margin: 15px 0 0 0;
                        border-radius: 15px;
                        box-shadow: 0 2px 25px #d7e9fb63;
                        padding: 20px 18px 18px 18px;
                        min-height: auto;
                        position: relative;
                        z-index: 1;
                    }
                    .invoice-details-row {
                        display: flex;
                        font-size: 19px;
                        margin-bottom: 13px;
                        font-weight: bold;
                        flex-direction: ${isRTL ? "row-reverse" : "row"};
                    }
                    .invoice-details-label {
                        width: 210px;
                        color: #295099;
                        opacity: 0.97;
                        text-align: ${isRTL ? "right" : "left"};
                    }
                    .invoice-details-value {
                        color: #252530;
                        flex: 1;
                        font-size: 19px;
                        font-weight: 700;
                        text-align: ${isRTL ? "left" : "right"};
                    }
                    .invoice-items-block {
                        margin: 34px 0 14px 0;
                        background: #f6fbff;
                        border-radius: 13px;
                        box-shadow: 0 1px 7px #e3f4fd55;
                        padding: 22px 19px 14px 19px;
                        border: 2px solid #e2eefd;
                    }
                    .invoice-items-header,
                    .invoice-item-row {
                        display: flex;
                        font-size: 1.12rem;
                        font-weight: 700;
                        border-bottom: 1.7px solid #e7efff;
                        padding: 7px 0;
                        align-items: center;
                        flex-direction: ${isRTL ? "row-reverse" : "row"};
                    }
                    .invoice-items-header {
                        color: #2850a7;
                        background: #eef4ff;
                        font-size: 1.16rem;
                    }
                    .invoice-item-col {
                        flex: 1;
                        min-width: 100px;
                        text-align: ${isRTL ? "right" : "left"};
                    }
                    .invoice-item-col.price,
                    .invoice-item-col.discount {
                        min-width: 90px;
                        text-align: ${isRTL ? "left" : "right"};
                    }
                    .invoice-summary-block {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                        margin-top: 34px;
                        margin-${isRTL ? "left" : "right"}: 24px;
                        margin-${isRTL ? "right" : "left"}: 0;
                    }
                    .invoice-summary-row {
                        display: flex;
                        font-size: 1.27rem;
                        margin-bottom: 3px;
                        font-weight: bold;
                        gap: 16px;
                        flex-direction: ${isRTL ? "row-reverse" : "row"};
                    }
                    .invoice-summary-label {
                        min-width: 88px;
                        color: #2850a7;
                        text-align: ${isRTL ? "right" : "left"};
                    }
                    .invoice-summary-value {
                        min-width: 110px;
                        text-align: ${isRTL ? "left" : "right"};
                        color: #222;
                    }
                    .invoice-footer {
                        padding: 32px 55px 12px 55px;
                        width: 100%;
                        color: #2850a7;
                        text-align: ${isRTL ? "left" : "right"};
                        font-weight: 700;
                        font-size: 18px;
                        border-top: 2px solid #f2f2fa;
                        margin-top: 45px;
                        background: #e7f1fa;
                        border-radius: 24px 24px 0 0;
                        letter-spacing: 0.7px;
                    }
                    `}
                </style>

                <div className="invoice-watermark">Service Energy</div>

                {/* Redesigned header as a table for better UX and bigger logos */}
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        marginBottom: 15,
                        background: '#fff',
                        borderRadius: isRTL ? '0 0 18px 18px' : '0 0 18px 18px',
                        boxShadow: '0 4px 25px #d4e3f5',
                        borderBottom: '3px solid #2850A7',
                        minHeight: 140,
                        direction: isRTL ? 'rtl' : 'ltr',
                    }}
                >
                    <tbody>
                        <tr>
                            {/* Left Logo */}
                            <td style={{ width: 140, textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', padding: 5 }}>
                                <img
                                    src={LOGO_URL}
                                    alt="Service Energy Logo"
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 16,
                                        objectFit: 'contain',
                                        background: '#fff',
                                        border: '2.5px solid #f2f3fa',
                                        boxShadow: '0 2px 16px rgba(40,80,167,0.12)',
                                        padding: 8,
                                        margin: isRTL ? '5px 0 5px 15px' : '5px 15px 5px 0',
                                        display: 'block',
                                        maxWidth: '120px',
                                        maxHeight: '120px'
                                    }}
                                />
                            </td>
                            {/* Center Content */}
                            <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '10px 8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
                                    <Title level={1} style={{ fontWeight: 900, color: '#2850A7', fontSize: '2.4rem', margin: 0, letterSpacing: 2, lineHeight: 1.1, textShadow: '0 2px 10px #e3f4fd', direction: isRTL ? 'rtl' : 'ltr' }}>Service Energy</Title>
                                    <Text style={{ fontWeight: 700, fontSize: 20, color: '#2850A7', marginBottom: 2 }}>{t('system')}</Text>
                                    <Text style={{ fontWeight: 500, fontSize: 16, color: '#568', marginBottom: 2 }}>{t('company_address')}</Text>
                                    <Text style={{ fontWeight: 500, fontSize: 16, color: '#568', marginBottom: 2 }}>07509927709 &nbsp;|&nbsp; 07769927700 &nbsp;|&nbsp; 07731187709</Text>
                                </div>
                            </td>
                            {/* Right Logo and Status */}
                            <td style={{ width: 140, textAlign: isRTL ? 'left' : 'right', verticalAlign: 'middle', padding: 5 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: isRTL ? 'flex-start' : 'flex-end', gap: 8 }}>
                                    <img
                                        src={logoURL}
                                        alt="Felicity Solar Logo"
                                        style={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: 16,
                                            objectFit: 'contain',
                                            background: '#fff',
                                            border: '2.5px solid #f2f3fa',
                                            boxShadow: '0 2px 16px rgba(40,80,167,0.12)',
                                            padding: 8,
                                            marginBottom: 8,
                                            display: 'block',
                                            maxWidth: '120px',
                                            maxHeight: '120px'
                                        }}
                                    />
                                    <div className="invoice-status" style={{ fontSize: '1.3rem', minWidth: 120, padding: '8px 16px' }}>{t(`status.${type}`)}</div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Main content as table for better UX */}
                <div className="invoice-main-content">
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginBottom: 18,
                            background: 'none',
                            direction: isRTL ? 'rtl' : 'ltr',
                        }}
                    >
                        <tbody>
                            <tr>
                                {/* Invoice Number & Date (RTL: order reversed) */}
                                {isRTL ? (
                                    <>
                                        <td style={{ width: 180, fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('date')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{new Date(invoice.date).toLocaleString(i18n.language)}</td>
                                        <td style={{ width: 180, fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('invoice_number')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.invoiceNumber}</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{ width: 180, fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('invoice_number')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.invoiceNumber}</td>
                                        <td style={{ width: 180, fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('date')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{new Date(invoice.date).toLocaleString(i18n.language)}</td>
                                    </>
                                )}
                            </tr>
                            <tr>
                                {/* Party & Currency (RTL: order reversed) */}
                                {isRTL ? (
                                    <>
                                        <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('currency')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.currency}</td>
                                        <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{partyLabel}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{customerName}</td>
                                    </>
                                ) : (
                                    <>
                                        <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{partyLabel}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{customerName}</td>
                                        <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('currency')}</td>
                                        <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.currency}</td>
                                    </>
                                )}
                            </tr>
                            {/* Phone & Note (RTL: order reversed) */}
                            {!isPurchase && (
                                <tr>
                                    {isRTL ? (
                                        <>
                                            <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('note')}</td>
                                            <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.note || '-'}</td>
                                            <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('phone')}</td>
                                            <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>
                                                {!isPurchase && "customer" in invoice && typeof invoice.customer === "object" && invoice.customer !== null
                                                    ? (invoice.customer as { phone?: string }).phone || '-'
                                                    : '-'}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('phone')}</td>
                                            <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>
                                                {"customer" in invoice && typeof invoice.customer === "object" && invoice.customer !== null && "phone" in invoice.customer
                                                    ? (invoice.customer as { phone?: string }).phone || '-'
                                                    : '-'}
                                            </td>
                                            <td style={{ fontWeight: 700, color: '#295099', fontSize: 18, padding: '7px 8px', background: '#f6fbff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('note')}</td>
                                            <td style={{ fontWeight: 700, color: '#252530', fontSize: 18, padding: '7px 8px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.note || '-'}</td>
                                        </>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Items Table */}
                    <div className="invoice-items-block" style={{ padding: 0 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'none', fontSize: '1.08rem' }}>
                            <thead>
                                <tr style={{ background: '#eef4ff', color: '#2850a7', fontWeight: 700 }}>
                                    <th style={{ padding: '8px 6px', borderBottom: '2px solid #e7efff', textAlign: 'center' }}>{t('items.product')}</th>
                                    <th style={{ padding: '8px 6px', borderBottom: '2px solid #e7efff', textAlign: 'center' }}>{t('items.unit')}</th>
                                    <th style={{ padding: '8px 6px', borderBottom: '2px solid #e7efff', textAlign: 'center' }}>{t('items.qty')}</th>
                                    <th style={{ padding: '8px 6px', borderBottom: '2px solid #e7efff', textAlign: 'center' }}>{t('items.price')}</th>
                                    <th style={{ padding: '8px 6px', borderBottom: '2px solid #e7efff', textAlign: 'center' }}>{t('items.total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.key} style={{ background: '#f6fbff', borderBottom: '1px solid #e7efff' }}>
                                        <td style={{ padding: '7px 6px', textAlign: 'center', fontWeight: 600 }}>{item.name}</td>
                                        <td style={{ padding: '7px 6px', textAlign: 'center' }}>{item.unit}</td>
                                        <td style={{ padding: '7px 6px', textAlign: 'center' }}>{item.qty}</td>
                                        <td style={{ padding: '7px 6px', textAlign: 'center', fontWeight: 600 }}>{item.price?.toLocaleString()}</td>
                                        {/* qty × price for this line, less any per-line discount */}
                                        <td style={{ padding: '7px 6px', textAlign: 'center', fontWeight: 700, color: '#2850a7' }}>
                                            {item.lineTotal.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#eef4ff', borderTop: '2px solid #e7efff' }}>
                                    <td colSpan={3} style={{ padding: '8px 6px', textAlign: isRTL ? 'left' : 'right', fontWeight: 700, color: '#2850a7' }}>
                                        {t('items.total')}
                                    </td>
                                    <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#2850a7' }}>
                                        {items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0).toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 800, color: '#2850a7' }}>
                                        {itemsTotal.toLocaleString()} {currency}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Summary Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 30, background: 'none' }}>
                        <tbody>
                            {discountAmount > 0 && (
                                <tr>
                                    <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 20, padding: '10px 12px', background: '#eef4ff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('subtotal')}</td>
                                    <td style={{ fontWeight: 700, color: '#222', fontSize: 20, padding: '10px 12px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{subtotalAmount?.toLocaleString()} {currency}</td>
                                    <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 20, padding: '10px 12px', background: '#eef4ff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('discount')}</td>
                                    <td style={{ fontWeight: 700, color: '#c0392b', fontSize: 20, padding: '10px 12px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>- {discountAmount?.toLocaleString()} {currency}</td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 20, padding: '10px 12px', background: '#eef4ff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('summary.total')}</td>
                                <td style={{ fontWeight: 700, color: '#222', fontSize: 20, padding: '10px 12px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{invoice.total?.toLocaleString()} {currency}</td>
                                {"paid" in invoice && (
                                    <>
                                        <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 20, padding: '10px 12px', background: '#eef4ff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('summary.paid')}</td>
                                        <td style={{ fontWeight: 700, color: '#222', fontSize: 20, padding: '10px 12px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{(invoice as any).paid?.toLocaleString()} {currency}</td>
                                    </>
                                )}
                                {"remain" in invoice && (
                                    <>
                                        <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 20, padding: '10px 12px', background: '#eef4ff', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{t('summary.remain')}</td>
                                        <td style={{ fontWeight: 700, color: '#222', fontSize: 20, padding: '10px 12px', border: '1.5px solid #e2eefd', borderRadius: 8 }}>{(invoice as any).remain?.toLocaleString()} {currency}</td>
                                    </>
                                )}
                            </tr>
                            {/* {(convertedTotal !== null || convertedToUSD !== null) && (
                                <tr>
                                    <td style={{ fontWeight: 700, color: '#2850a7', fontSize: 18, padding: '8px 12px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 8 }}>
                                        {t('summary.equivalent')}
                                    </td>
                                    <td colSpan={5} style={{ fontWeight: 700, color: '#166534', fontSize: 18, padding: '8px 12px', border: '1.5px solid #bbf7d0', borderRadius: 8 }}>
                                        {convertedTotal !== null
                                            ? `≈ ${convertedTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} IQD`
                                            : `≈ ${convertedToUSD!.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD`}
                                    </td>
                                </tr>
                            )} */}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
);

export default PrintableInvoice;
